/**
 * OWL2 Ontology Container
 *
 * Defines the root Ontology structure that contains all axioms,
 * declarations, prefixes, imports, and annotations.
 */
// =============================================================================
// Factory Functions
// =============================================================================
/**
 * Create a prefix declaration.
 */
export function prefixDeclaration(name, iri) {
    return { name, iri };
}
/**
 * Create an import declaration.
 */
export function importDeclaration(iri) {
    return { iri };
}
/**
 * Create an empty ontology.
 */
export function emptyOntology() {
    return {
        prefixes: [],
        imports: [],
        annotations: [],
        axioms: []
    };
}
/**
 * Create an ontology with the given IRI.
 */
export function ontology(ontologyIRI, options) {
    return {
        ...(ontologyIRI && { ontologyIRI }),
        ...(options?.versionIRI && { versionIRI: options.versionIRI }),
        prefixes: options?.prefixes ?? [],
        imports: options?.imports ?? [],
        annotations: options?.annotations ?? [],
        axioms: options?.axioms ?? []
    };
}
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Add a prefix declaration to an ontology (returns new ontology).
 */
export function addPrefix(onto, prefix) {
    return {
        ...onto,
        prefixes: [...onto.prefixes, prefix]
    };
}
/**
 * Add an import declaration to an ontology (returns new ontology).
 */
export function addImport(onto, imp) {
    return {
        ...onto,
        imports: [...onto.imports, imp]
    };
}
/**
 * Add an annotation to an ontology (returns new ontology).
 */
export function addOntologyAnnotation(onto, ann) {
    return {
        ...onto,
        annotations: [...onto.annotations, ann]
    };
}
/**
 * Add an axiom to an ontology (returns new ontology).
 */
export function addAxiom(onto, axiom) {
    return {
        ...onto,
        axioms: [...onto.axioms, axiom]
    };
}
/**
 * Add multiple axioms to an ontology (returns new ontology).
 */
export function addAxioms(onto, axioms) {
    return {
        ...onto,
        axioms: [...onto.axioms, ...axioms]
    };
}
/**
 * Get all axioms of a specific type from an ontology.
 */
export function getAxiomsOfType(onto, predicate) {
    return onto.axioms.filter(predicate);
}
/**
 * Find a prefix by name.
 */
export function findPrefix(onto, name) {
    return onto.prefixes.find(p => p.name === name);
}
/**
 * Expand an abbreviated IRI using the ontology's prefix declarations.
 */
export function expandIRI(onto, abbreviated) {
    const colonIndex = abbreviated.indexOf(':');
    if (colonIndex === -1)
        return undefined;
    const prefixName = abbreviated.substring(0, colonIndex);
    const localName = abbreviated.substring(colonIndex + 1);
    const prefix = findPrefix(onto, prefixName);
    if (!prefix)
        return undefined;
    return prefix.iri + localName;
}
